import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import {
  adminActions,
  authSelectors,
  useDispatch,
  useSelector,
} from "../../state";
import { CloseIcon, SearchIcon } from "../icons";
import { history } from "../../lib";
import { useStyles } from "./MobileSearchView.styles";

type Props = {
  onClose: () => void;
};

type SearchResult = {
  key: string;
  req_uid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  agency?: { domain?: string };
};

const MobileSearchView: React.FC<Props> = ({ onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const agencyId = useSelector(authSelectors.activeAgencyId);
  const activeRole = useSelector(authSelectors.activeRole);

  const [value, setValue] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  // Auto-focus the field on open. Keep the keyboard open on mobile.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Esc closes (matches Gmail/YouTube pattern).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll while open so the page beneath doesn't move.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Debounced search.
  useEffect(() => {
    const trimmed = value.trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    if (!activeRole || !agencyId) {
      return undefined;
    }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      const data: any = await dispatch(
        adminActions.searchQuotes(trimmed, activeRole, agencyId),
      );
      setResults(Array.isArray(data) ? data : []);
      setLoading(false);
    }, 250);
    return () => {
      window.clearTimeout(timer);
      setLoading(false);
    };
  }, [value, activeRole, agencyId, dispatch]);

  const handleSelect = (item: SearchResult) => {
    onClose();
    history.push("/admin/quote-management/" + item.req_uid);
  };

  const trimmedQuery = value.trim();
  const showEmpty = !trimmedQuery;
  const showNoResults = !!trimmedQuery && !loading && results.length === 0;

  return (
    <Box className={classes.root} role="dialog" aria-label="Search">
      <Box className={classes.topBar}>
        <TextField
          inputRef={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Search quote by name, email, or phone"
          fullWidth
          size="small"
          autoFocus
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={classes.searchIcon} />
              </InputAdornment>
            ),
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => {
                    setValue("");
                    inputRef.current?.focus();
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <IconButton
          aria-label="Close search"
          onClick={onClose}
          size="large"
          className={classes.closeBtn}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.body}>
        {loading && (
          <Box className={classes.loaderRow}>
            <CircularProgress size={20} />
            <Typography className={classes.loaderText}>Searching…</Typography>
          </Box>
        )}

        {showEmpty && !loading && (
          <Box className={classes.emptyState}>
            <SearchIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              Search applications
            </Typography>
            <Typography className={classes.emptyHint}>
              Find quotes by customer name, email, or phone number.
            </Typography>
          </Box>
        )}

        {showNoResults && (
          <Box className={classes.emptyState}>
            <Typography className={classes.emptyTitle}>
              No results for &ldquo;{trimmedQuery}&rdquo;
            </Typography>
            <Typography className={classes.emptyHint}>
              Try a different name, email, or phone number.
            </Typography>
          </Box>
        )}

        {!loading && results.length > 0 && (
          <List disablePadding className={classes.results}>
            {results.map((item) => (
              <ListItem key={item.key} disablePadding divider>
                <ListItemButton
                  onClick={() => handleSelect(item)}
                  className={classes.resultItem}
                >
                  <ListItemText
                    primary={
                      <Typography className={classes.resultName}>
                        {item.first_name} {item.last_name}
                      </Typography>
                    }
                    secondary={
                      <Typography className={classes.resultMeta}>
                        {item.email}
                        {item.phone ? ` · ${item.phone}` : ""}
                        {item.agency?.domain ? ` · ${item.agency.domain}` : ""}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default MobileSearchView;
